<template>
    <div class="flex gap-6 laboratory-horse-test-outer">
        <LibyanOrnament vertical />
        <div v-if="loaded" class="laboratory-horse-test flex flex-column gap-8 w-11">
            <VeeForm @submit="saveTest"
                     v-slot="{ isSubmitting }"
                     :validateOnMount="false"
                     :validateOnInput="false"
                     :validateOnBlur="false">
                <div class="flex justify-content-between align-items-center">
                    <h2 class="laboratory-horse-test-header m-0">{{ $t('message.laboratoryHorses.form.header') }}</h2>
                    <div class="flex justify-content-center mt-4">
                        <CustomButton
                            type="submit"
                            :label="$t('message.laboratoryHorses.form.save')"
                            secondary
                            :disabled="isSubmitting"
                        />
                    </div>
                </div>

                <div class="flex flex-column first-section">
                    <h2 class="section-header mt-0">{{ testHorse.name }}</h2>
                    <div class="flex align-items-center section-details">
                        <div class="w-6 flex flex-column gap-3 section-details-inner">
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.horseId') }}</span>
                                {{ testHorse.ueln }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.gender') }}</span>
                                {{ testHorse.sex }}
                            </div>
                            <div class="data-box">
                                <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.coatColor') }}</span>
                                {{ testHorse.color }}
                            </div>
                        </div>
                        <div class="w-6 flex flex-column gap-3 test-box section-details-inner">
                            <CustomSelectOne
                                name="testResult"
                                :label="$t('message.laboratoryHorses.form.testRes')"
                                required
                                noPlaceholder
                                :items="testResults"
                                v-model="laboratoryTestResult"
                                rules="required"
                            />
                            <CustomSelectOne
                                v-if="showWhyLabel(laboratoryTestResult)"
                                name="why"
                                :label="$t('message.laboratoryHorses.form.why')"
                                required
                                noPlaceholder
                                :items="computedStatuses"
                                v-model="laboratoryTestStatus"
                                :key="laboratoryTestResult"
                                itemValue="value"
                                rules="required"
                            />
                        </div>
                    </div>
                    <div>
                        <h2>{{$t('message.laboratoryHorses.form.dnaCard')}}
                            <a :href="testHorse.horseDnaDocumentUrl" v-if="testHorse.horseDnaDocumentUrl"
                               download target="_blank" v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"
                               style="color: #192134">
                                <i class="pi pi-cloud-download download-button"/>
                            </a>
                        </h2>
                        <CustomDnaCardUploader :url="horseUploadUrl" name="dnaResult"/>
                    </div>
                </div>

                <div class="second-section">
                    <div v-for="(dnaSample, index) in testHorse.laboratoryTest" :key="dnaSample.id" class="flex flex-column justify-content-center">
                        <h2 class="section-header dna-header">
                            {{ $t('message.laboratoryHorses.form.dna') }} #{{ testHorse.laboratoryTest.length - index }}
                        </h2>
                        <div class="flex section-details">
                            <div class="w-6 flex flex-column gap-3 section-details-inner">
                                <div class="data-box">
                                    <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.samplesFrom') }}</span>
                                    {{ dnaSample.dnaSampleTypesLabels.join(', ') }}
                                </div>
                                <div class="data-box">
                                    <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.batchRef') }}</span>
                                    {{ dnaSample.batchRefNumber }}
                                </div>
                                <div class="data-box">
                                    <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.testReq') }}</span>
                                    {{ computeTestTypeLabel(dnaSample.testType) }}
                                </div>
                                <div class="data-box">
                                    <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.testPrior') }}</span>
                                    {{ dnaSample.testPriority }}
                                </div>
                                <div class="data-box">
                                    <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.shipment') }}</span>
                                    {{ dnaSample.shipmentDate }}
                                </div>
                            </div>
                            <div class="w-6 gap-3 flex flex-column section-details-inner">
                                <CustomInputText
                                    :name="`testHorse.laboratoryTest[${index}].dnaReferenceNumber`"
                                    :label="$t('message.laboratoryHorses.form.dnaRef')"
                                    :required = "requiredForNotInProgress"
                                    noPlaceholder
                                    v-model="dnaSample.dnaReferenceNumber"
                                    :disabled="disableEdit(dnaSample)"
                                    :rules="!requiredForNotInProgress || disableEdit(dnaSample) ? undefined : 'required'"
                                    :key="laboratoryTestResult"
                                />
                                <CustomCalendar
                                    :name="`testHorse.laboratoryTest[${index}].shipmentReceivedDate`"
                                    :label="$t('message.laboratoryHorses.form.receipt')"
                                    noPlaceholder
                                    v-model="dnaSample.shipmentReceivedDate"
                                    :disabled="disableEdit(dnaSample) || dnaSample.shipmentReceivedDate != null"
                                    :key="laboratoryTestResult"
                                />
                                <CustomInputTextArea
                                    :name="`testHorse.laboratoryTest[${index}].notes`"
                                    :label="$t('message.laboratoryHorses.form.comments')"
                                    v-model="dnaSample.notes"
                                    :disabled="disableEdit(dnaSample)"
                                    noPlaceholder
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex third-section section-details">
                    <div class="w-6 flex flex-column gap-3 section-details-inner">
                        <h2 class="section-header parents-header m-0">{{ $t('message.laboratoryHorses.form.sire') }}</h2>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.name') }}</span>
                            {{ testHorse.father.name }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.horseId') }}</span>
                            {{ testHorse.father.ueln }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.coatColor') }}</span>
                            {{ testHorse.father.color }}
                        </div>
                        <div v-if="testHorse.father.birthCountry === 'Libya'" class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaRef') }}</span>
                            {{ testHorse.father.dnaReference }}
                        </div>
                    </div>
                    <div class="w-6 flex flex-column gap-3 section-details-inner">
                        <h2 class="section-header parents-header m-0">{{ $t('message.laboratoryHorses.form.dam') }}</h2>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.name') }}</span>
                            {{ testHorse.mother.name }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.horseId') }}</span>
                            {{ testHorse.mother.ueln }}
                        </div>
                        <div class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.coatColor') }}</span>
                            {{ testHorse.mother.color }}
                        </div>
                        <div v-if="testHorse.mother.birthCountry === 'Libya'" class="data-box">
                            <span class="uppercase label-font">{{ $t('message.laboratoryHorses.form.dnaRef') }}</span>
                            {{ testHorse.mother.dnaReference }}
                        </div>
                    </div>
                </div>

                <div class="dna-cards flex gap-3 mt-5">
                    <div class="w-6" v-if="testHorse.sireDnaDocumentUrl" >
                        <h2>{{$t('message.horseCreate.sireDnaCard')}}
                            <a :href="testHorse.sireDnaDocumentUrl"
                                download target="_blank" v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"
                                style="color: #192134">
                                <i class="pi pi-cloud-download download-button"/>
                            </a>
                        </h2>
                    </div>
                    <div class="w-6" v-if="testHorse.damDnaDocumentUrl">
                        <h2>{{$t('message.horseCreate.damDnaCard')}}
                            <a :href="testHorse.damDnaDocumentUrl"
                                download target="_blank" v-tooltip.top="$t('message.horseCreate.downloadDnaCard')"
                                style="color: #192134">
                                <i class="pi pi-cloud-download download-button"/>
                            </a>
                        </h2>

                    </div>
                </div>
            </VeeForm>
        </div>
    </div>
</template>

<script>
    import CustomSelectOne from '@/components/form/inner/CustomSelectOne.vue';
    import CustomInputText from '@/components/form/CustomInputText.vue';
    import CustomCalendar from '@/components/form/CustomCalendar.vue';
    import CustomInputTextArea from '@/components/form/CustomInputTextArea.vue';
    import LibyanOrnament from '@/components/LibyanOrnament.vue';
    import CustomButton from '@/components/form/CustomButton.vue';
    import { Form as VeeForm } from 'vee-validate';
    import {
        getHorseView,
        getTestResults,
        getTestStatuses,
        getTestType,
        fillHorseTest,
        getHorseViewForLaboratoryUser
    } from "@/swagger/vue-api-client";
    import _ from 'lodash';
    import Tooltip from "primevue/tooltip";
    import {SystemRole} from "@/utils/SystemRole";
    import CustomDnaCardUploader from "@/components/form/CustomDnaCardUploader.vue";

    export default {
        name: "LaboratoryHorseTestFormView",

        components: {
            CustomDnaCardUploader,
            CustomSelectOne,
            CustomInputText,
            CustomCalendar,
            CustomInputTextArea,
            LibyanOrnament,
            CustomButton,
            VeeForm
        },

        data() {
            return {
                testHorse: null,
                loaded: false,
                testResults: [],
                testStatuses: [],
                testTypes: [],
                laboratoryTestResult: null,
                laboratoryTestStatus: null,
                testStatus: null,
                computedStatuses: [],
                fetchHorse: SystemRole.getUserRole() === SystemRole.LAB_USER ? getHorseViewForLaboratoryUser : getHorseView
            };
        },

        beforeMount() {
            Promise.all([
                getTestResults().then(res => { this.testResults = res.data; }),
                getTestStatuses().then(res => { this.testStatuses = res.data; }),
                getTestType().then(res => { this.testTypes = res.data; }),
                this.fetchHorse({id: this.$route.params.id}).then(res => {
                    this.testHorse = res.data;
                    this.laboratoryTestResult = this.testResults.find(r => r.value === this.testHorse.laboratoryTest.at(0)?.laboratoryTestResult);
                    this.laboratoryTestStatus = this.testStatuses.find(s => s.value === this.testHorse.laboratoryTest.at(0)?.laboratoryTestStatus);
                })
            ]).then(() => {
                this.loaded = true;
            }).catch(error => {
                this.notifyError(error);
            });
        },

        directives: {
            tooltip: Tooltip,
        },

        methods: {
            async saveTest() {
                try {
                    const body = _.cloneDeep(this.testHorse.laboratoryTest[0]);
                    body.laboratoryTestResult = this.laboratoryTestResult.value;
                    body.laboratoryTestStatus = this.laboratoryTestStatus;

                    await fillHorseTest({ body: body });

                    this.$toast.add({
                        severity: "success",
                        summary: this.$t('message.other.success'),
                        detail: this.$t('message.other.dataSaved'),
                        life: 3000,
                    });
                    this.$router.push({path: "/pending/list"});
                } catch (err) {
                    window.console.error(err)
                    this.notifyError(err);
                }
            },

            notifyError(error) {
                if (error.response?.data) {
                    for (const [key, value] of Object.entries(error.response.data)) {
                        if (key !== "path" && key !== "status" && key !== "timestamp") {
                            this.$toast.add({
                                severity: "error",
                                summary: this.$t("message.other.error"),
                                detail: value,
                                life: 5000,
                            });
                        }
                    }
                } else {
                    this.$toast.add({
                        severity: "error",
                        summary: this.$t("message.other.error"),
                        detail: this.$t("unknownError"),
                        life: 5000,
                    });
                }
            },

            disableEdit(dna) {
                return dna?.laboratoryTestResult === 'PARENTAGE_VERIFIED' ||
                    dna?.laboratoryTestResult === 'PARENTAGE_NOT_VERIFIED' ||
                    dna?.laboratoryTestResult === 'NEEDS_RECOLLECTION';
            },

            showWhyLabel(result) {
                return result?.value === 'PARENTAGE_NOT_VERIFIED' || result?.value === 'NEEDS_RECOLLECTION';
            },

            computeTestTypeLabel(type) {
                const foundType = this.testTypes.find(t => t.value === type);
                return foundType ? foundType.label : '';
            }
        },

        computed: {
            requiredForNotInProgress: function () {
                return this.laboratoryTestResult?.value !== 'IN_PROGRESS';
            },
            horseUploadUrl() {
                return `${process.env.VUE_APP_API_URL}/files/dna-card/` + this.testHorse.id
            },
        },

        watch: {
            laboratoryTestResult: {
                handler(testRes) {
                    this.computedStatuses = [];
                    if (testRes) {
                        const regularStatuses = [];
                        const otherStatuses = [];

                        this.testStatuses.forEach((tS) => {
                            if (testRes.value === tS.extendedValue) {
                                if (tS.value === 'OTHER') {
                                    otherStatuses.push(tS);
                                } else {
                                    regularStatuses.push(tS);
                                }
                            }
                        });
                        this.computedStatuses = [...regularStatuses, ...otherStatuses];
                    }
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss">
    .laboratory-horse-test {
        font-family: var(--decor-font-family);
        .custom-input-text, .custom-calendar, .custom-select-one, .custom-input-textarea{
            padding-left: 170px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 160px;
                text-align: right;
                font-family: var(--font-family);
            }
        }
        .custom-calendar {
            .p-calendar {
                width: 100%;
            }
        }

        .test-box {
            background-color: var(--primary-color);
            padding: 1.5rem;
            border-radius: var(--border-radius);
            .p-inputtext, .p-dropdown, .p-inputtext:hover, .p-dropdown:not(.p-disabled):hover {
                background-color: var(--primary-color);
                border-color: var(--text-color);
                .p-dropdown-trigger {
                    color: var(--text-color);
                }
            }
        }
        &-header {
            font-size: 35px;
            color: var(--accent-color);
        }
        .section-header {
            font-size: 35px;
            &.dna-header {
                font-size: 25px;
                color: var(--accent-color);
            }
            &-parents-header {
                font-size: 30px;
            }
        }
        h3 {
            margin-top: 3rem;
            margin-bottom: 0;
            &.subsection-header__first {
                padding-left: 98px;
            }
            &.subsection-header__second {
                padding-left: 20px;
            }
            &.subsection-header__third {
                padding-left: 75px;
            }
        }
        .data-box {
            font-size: 25px;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            .label-font {
                font-family: var(--font-family);
                font-size: 14px;
                line-height: 14px;
            }
        }
        .first-section, .second-section {
            position: relative;
            &::after {
                content: "";
                border: 1px solid var(--separator-color);
                position: absolute;
                bottom: -3.5rem;
                left: 0;
                width: 100%;
            }
        }
        @media screen and (max-width: 1000px) {
            .section-details {
                flex-direction: column;
                align-items: start !important;
                gap: 2rem;
                &-inner {
                    width: 100% !important;
                }
            }
        }
    }
    @media screen and (max-width: 650px) {
        .laboratory-horse-test-outer {
            margin: 20px 10px;
            .custom-input-text, .custom-calendar, .custom-select-one, .custom-input-textarea{
                padding-left: 100px;
                .custom-label {
                    left: 90px;
                }
            }
        }
    }

    a > i.download-button {
        font-size: 24px;
    }
</style>
